import React from 'react';
import {Icon} from '@iconify/react';

import {getSocialPlatformNameByPostType} from 'utils/gigUtils';

import {numberWithCommas} from './utilities';

interface Percent {
  value: number;
  formattedValue: string;
}

interface InstagramAccount {
  username?: string;
  followers?: number;
  engagement?: Percent;
}

interface TikTokAccount {
  username?: string;
  followers?: number;
  engagementRate90days?: Percent;
}

interface Influencer {
  instagramAccount?: InstagramAccount;
  tiktokAccount?: TikTokAccount;
}

interface Post {
  postType: string;
}

interface FollowerCount {
  [platform: string]: number | undefined;
}

interface Offer {
  influencer: Influencer;
}

const IG_ENGAGEMENT_RATE_LOWER_LIMIT = 0.01;
const IG_ENGAGEMENT_RATE_UPPER_LIMIT = 0.15;

export function getEngagementExplain(totalEngagement: number): JSX.Element {
  let text = 'Normal engagement';
  if (totalEngagement < IG_ENGAGEMENT_RATE_LOWER_LIMIT) {
    text = 'Total engagement is very low';
  }
  if (totalEngagement > IG_ENGAGEMENT_RATE_UPPER_LIMIT) {
    text = 'Total engagement is very high';
  }
  return <div>{text}</div>;
}

export function isEngagementLimitExceeded(totalEngagement: number): boolean {
  return (
    totalEngagement < IG_ENGAGEMENT_RATE_LOWER_LIMIT ||
    totalEngagement > IG_ENGAGEMENT_RATE_UPPER_LIMIT
  );
}

export function getLocation(regionName: string, regionCountry: string): string {
  if (regionName !== regionCountry) {
    return `${regionName}, ${regionCountry}`;
  }

  return regionName;
}

/**
 *
 * @param followerCount Object containing follower counts for different platforms
 * @returns {JSX.Element} HTML to display to user
 */
export function multiPlatformFollowerCount(followerCount: FollowerCount): JSX.Element {
  const formatPlatformName = (name: string): string => {
    if (name.toLowerCase() === 'instagram') return 'Instagram';
    else if (name.toLowerCase() === 'tiktok') return 'TikTok';
    return name;
  };

  return (
    <div>
      {Object.entries(followerCount).map(([platform, followerNumber]) => (
        <p key={platform}>
          <b>{formatPlatformName(platform)}:</b>{' '}
          {followerNumber ? numberWithCommas(followerNumber) : 'N/A'}
        </p>
      ))}
    </div>
  );
}

/**
 * Returns the username for a given platform and influencer.
 * @param {string} platform - "instagram", "tiktok", "youtube"
 * @param {Influencer} influencer
 * @returns {string|null}
 */
export const usernameForPlatform = (platform: string, influencer: Influencer): string | null => {
  switch (platform.toLowerCase()) {
    case 'instagram':
      return influencer.instagramAccount?.username || null;
    case 'tiktok':
      return influencer.tiktokAccount?.username || null;
    // case 'youtube':
    //   return influencer.youtubeAccount?.username;
    default:
      return null;
  }
};

/**
 * Returns the username for a given post and influencer.
 * @param {Post} post - A Gig Post object.
 * @param {Influencer} influencer - A Influencer object.
 * @returns {string|null}
 */
export const usernameForPost = (post: Post, influencer: Influencer): string | null => {
  return usernameForPlatform(
    getSocialPlatformNameByPostType(post.postType)?.toLowerCase() || '',
    influencer
  );
};

export function campaignParticipantPlatformFollowerCount(
  followerCount: FollowerCount
): JSX.Element {
  const icon = (name: string): string => {
    if (name.toLowerCase() === 'instagram') return 'mdi:instagram';
    else if (name.toLowerCase() === 'tiktok') return 'ic:baseline-tiktok';
    return '';
  };
  return (
    <div>
      {Object.entries(followerCount).map(([platform, followerNumber]) => {
        if (followerNumber === undefined || followerNumber === 0) {
          return null;
        }
        return (
          <p key={platform} style={{display: 'flex', alignItems: 'center'}}>
            <Icon icon={icon(platform)} style={{height: '20px', width: '20px', margin: '5px'}} />
            {numberWithCommas(followerNumber)}
          </p>
        );
      })}
    </div>
  );
}

export const sumMultiPlatform = (accumulator: FollowerCount, offer: Offer): FollowerCount => ({
  instagram: (accumulator?.instagram || 0) + (offer.influencer.instagramAccount?.followers || 0),
  tiktok: (accumulator?.tiktok || 0) + (offer.influencer.tiktokAccount?.followers || 0),
});

/**
 *
 * @param {Offer} offer
 * @returns {number} Total number of followers across all tracked platforms
 */
export function getTotalFollowersForOffer(offer: Offer): number {
  return (
    (offer.influencer.instagramAccount?.followers || 0) +
    (offer.influencer.tiktokAccount?.followers || 0)
  );
}

export const talentTierConfig = {
  untiered: {
    label: 'No Tier',
    icon: <Icon icon="mdi:hourglass-empty" />,
    color: 'primary',
  },
  nano: {
    label: 'Nano',
    icon: <Icon icon="mdi:star-border" />,
    color: 'primary',
  },
  micro: {
    label: 'Micro',
    icon: <Icon icon="mdi:person" />,
    color: 'primary',
  },
  mid: {
    label: 'Mid',
    icon: <Icon icon="mdi:groups" />,
    color: 'primary',
  },
  macro: {
    label: 'Macro',
    icon: <Icon icon="mdi:trending-up" />,
    color: 'primary',
  },
  mega: {
    label: 'Mega',
    icon: <Icon icon="mdi:public" />,
    color: 'primary',
  },
  celebrity: {
    label: 'Celebrity',
    icon: <Icon icon="mdi:verified" />,
    color: 'primary',
  },
};
